import { useState, useRef, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { Plus, Download, Upload, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableChartCard } from './SortableChartCard';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import type { Tool, Trace } from '@/features/traceability/type/Tool';
import { DEFAULT_CHART, DEFAULT_ANALYTICS_CHART } from '../../utils/chart';
import type { ChartConfig, ChartMode } from '../../type/dynamicChart';
import { validateChartConfig, getDefaultConfigForChartType } from '../../utils/chartDefaults';
import {
  exportChartConfig,
  importChartConfig,
  saveDefaultConfig,
  loadDefaultConfig,
  clearDefaultConfig,
} from '../../utils/chartConfigStorage';

type ComplexChartsProps = {
  tools: Tool[];
  isLoading: boolean;
  mode?: ChartMode;
};

export function ComplexCharts({ tools, isLoading, mode = 'analytics' }: ComplexChartsProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();
  const defaultChart = mode === 'analytics' ? DEFAULT_ANALYTICS_CHART : DEFAULT_CHART;
  // The default chart's title is a translation key (utils/chart.ts).
  const [charts, setCharts] = useState<ChartConfig[]>(() => [
    { ...defaultChart, title: t(defaultChart.title) },
  ]);
  const [hasDefault, setHasDefault] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load default configuration on mount
  useEffect(() => {
    const defaultConfig = loadDefaultConfig(mode);
    if (defaultConfig && defaultConfig.length > 0) {
      setCharts(defaultConfig);
      setHasDefault(true);
    }
  }, [mode]);

  // Get all traces from tools, enriched with resolved reference label for chart display
  const allTraces: (Trace & { reference?: string })[] = tools.flatMap((tool) =>
    tool.traces.map((trace) => ({
      ...trace,
      toolId: tool.toolId,
      reference: tool.reference ? resolve(tool.reference).label : undefined,
    }))
  );

  // Sort charts by layout.order before rendering
  const sortedCharts = [...charts].sort((a, b) => a.layout.order - b.layout.order);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCharts((prev) => {
      const sorted = [...prev].sort((a, b) => a.layout.order - b.layout.order);
      const oldIndex = sorted.findIndex((c) => c.id === active.id);
      const newIndex = sorted.findIndex((c) => c.id === over.id);
      const reordered = arrayMove(sorted, oldIndex, newIndex);
      return reordered.map((c, i) => ({ ...c, layout: { ...c.layout, order: i * 1000 } }));
    });
  }

  const handleAddChart = () => {
    setCharts([
      ...charts,
      {
        ...defaultChart,
        id: crypto.randomUUID(),
        title: t('analytics.dashboard.complexCharts.chartNumber', { number: charts.length + 1 }),
        layout: { size: 'md', order: charts.length * 1000 },
      },
    ]);
  };

  const handleRemoveChart = (chartId: string) => {
    if (charts.length === 1) return;
    setCharts(charts.filter((c) => c.id !== chartId));
  };

  const handleUpdateChart = (chartId: string, updates: Partial<ChartConfig>) => {
    setCharts(
      charts.map((c) => {
        if (c.id !== chartId) return c;

        let updatedChart = { ...c, ...updates };

        if (updates.type && updates.type !== c.type) {
          const defaults = getDefaultConfigForChartType(updates.type, mode);
          updatedChart = {
            ...c,
            ...updates,
            ...defaults,
            id: c.id,
            title: c.title,
            filters: c.filters,
            filterMode: c.filterMode,
            layout: c.layout, // preserve layout when switching chart type
          };
        }

        if (updatedChart.type === 'bar' && !updatedChart.options.groupBy) {
          updatedChart.options.groupBy = mode === 'comparison' ? 'reference' : undefined;
        }

        if (updatedChart.type !== 'bar' && updatedChart.options.groupBy) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { groupBy, ...restOptions } = updatedChart.options;
          updatedChart.options = restOptions;
        }

        return validateChartConfig(updatedChart, mode);
      })
    );
  };

  const handleExport = () => {
    try {
      exportChartConfig(charts, mode);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const collection = await importChartConfig(file, mode);
      if (!collection) return;
      setCharts(collection.configs);
    } catch (error) {
      console.error('Import failed:', error);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSaveDefault = () => {
    try {
      saveDefaultConfig(charts, mode);
      setHasDefault(true);
    } catch (error) {
      console.error('Save default failed:', error);
    }
  };

  const handleClearDefault = () => {
    try {
      clearDefaultConfig(mode);
      setHasDefault(false);
    } catch (error) {
      console.error('Clear default failed:', error);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="section-title">{t('analytics.dashboard.complexCharts.title')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('analytics.dashboard.complexCharts.description')}
            </p>
          </div>
        </div>
        <Skeleton className="h-[600px] rounded-xl" />
      </section>
    );
  }

  if (tools.length === 0) {
    return null;
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="section-title">{t('analytics.dashboard.complexCharts.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('analytics.dashboard.complexCharts.description')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Import/Export/Save buttons */}
          <div className="flex items-center gap-1">
            <IconButton
              icon={Download}
              label={t('analytics.dashboard.complexCharts.exportConfig')}
              onClick={handleExport}
            />
            <IconButton
              icon={Upload}
              label={t('analytics.dashboard.complexCharts.importConfig')}
              onClick={() => fileInputRef.current?.click()}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <IconButton
              icon={Save}
              label={t('analytics.dashboard.complexCharts.saveAsDefault')}
              onClick={handleSaveDefault}
            />
            {hasDefault && (
              <IconButton
                icon={Trash2}
                label={t('analytics.dashboard.complexCharts.clearDefault')}
                onClick={handleClearDefault}
              />
            )}
          </div>

          <Button onClick={handleAddChart}>
            <Plus aria-hidden />
            {t('analytics.dashboard.complexCharts.addChart')}
          </Button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortedCharts.map((c) => c.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-12 gap-4">
            {sortedCharts.map((chart) => (
              <SortableChartCard
                key={chart.id}
                chart={chart}
                traces={allTraces}
                tools={tools}
                onUpdate={(updates: Partial<ChartConfig>) => handleUpdateChart(chart.id, updates)}
                onRemove={() => handleRemoveChart(chart.id)}
                canRemove={charts.length > 1}
                mode={mode}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
}
